/**
 * Device Detection Utilities
 * Used to detect mobile devices and check browser API availability
 */

/**
 * Detect if user is on a mobile device
 * @returns true if on mobile, false otherwise
 */
export const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false

  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera

  // Mobile patterns - including more Android variants
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i

  return (
    mobileRegex.test(userAgent.toLowerCase()) ||
    window.innerWidth <= 768 ||
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0
  )
}

/**
 * Detect Android device specifically
 * @returns true if on Android device
 */
export const isAndroidDevice = (): boolean => {
  if (typeof window === 'undefined') return false

  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera
  return /android/i.test(userAgent.toLowerCase())
}

/**
 * Get Android version if on Android device
 * @returns Android version number or null
 */
export const getAndroidVersion = (): number | null => {
  if (!isAndroidDevice()) return null

  const userAgent = navigator.userAgent || ''
  const match = userAgent.match(/Android\s+([\d.]+)/)

  if (match && match[1]) {
    return parseFloat(match[1])
  }

  return null
}

/**
 * Check if on iOS device
 * @returns true if on iOS
 */
export const isIOSDevice = (): boolean => {
  if (typeof window === 'undefined') return false

  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera
  return /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream
}

/**
 * Check if Phantom wallet is ready and methods are available
 * Enhanced for Android compatibility
 * @returns true if Phantom is loaded with all required methods
 */
export const isPhantomReady = (): boolean => {
  try {
    const phantom = (window as any).phantom?.solana
    if (!phantom || !phantom.isPhantom) return false

    // Verify critical methods exist to avoid "Method not found" errors
    const hasRequiredMethods = (
      typeof phantom.connect === 'function' &&
      typeof phantom.disconnect === 'function' &&
      typeof phantom.on === 'function' &&
      typeof phantom.off === 'function'
    )

    if (!hasRequiredMethods) return false

    // Additional check for Android devices - some Android browsers may have phantom
    // object but methods aren't fully initialized yet
    if (isAndroidDevice()) {
      const androidVersion = getAndroidVersion()

      // Android 6 and below may have issues with Phantom API
      if (androidVersion && androidVersion < 7) {
        console.warn('[Device] Android version < 7 detected - Phantom may have limited support')
        // Still return true if methods exist, but logged warning
      }

      // Check if methods are actually callable (not just defined)
      try {
        // Test if connect method is a real function
        if (phantom.connect.toString().includes('[native code]') === false) {
          // Check if it looks like a proper function
          const fnString = phantom.connect.toString()
          if (fnString === '' || fnString === 'undefined') {
            console.warn('[Device] Phantom methods appear to be stubs on this Android device')
            return false
          }
        }
      } catch {
        // If we can't check, assume it's okay
      }
    }

    return true
  } catch (error) {
    console.warn('[Device] Error checking Phantom readiness:', error)
    return false
  }
}

/**
 * Wait for Phantom to be ready with retries
 * Useful for Android devices where Phantom may take longer to initialize
 * @param maxAttempts Maximum number of retry attempts
 * @param delayMs Delay between attempts in milliseconds
 * @returns Promise that resolves when Phantom is ready or rejects after max attempts
 */
export const waitForPhantomReady = async (
  maxAttempts: number = 10,
  delayMs: number = 500
): Promise<boolean> => {
  for (let i = 0; i < maxAttempts; i++) {
    if (isPhantomReady()) {
      console.log(`[Phantom] Ready after ${i + 1} attempt(s)`)
      return true
    }

    // Wait before next attempt
    await new Promise(resolve => setTimeout(resolve, delayMs))
  }

  console.warn(`[Phantom] Not ready after ${maxAttempts} attempts`)
  return false
}

/**
 * Safe wrapper for Phantom API calls
 * Catches errors and logs them appropriately
 * Enhanced with retry logic for Android devices
 */
export const safePhantomCall = async <T>(
  fn: () => Promise<T>,
  fallback?: T,
  errorMessage?: string
): Promise<T | undefined> => {
  try {
    return await fn()
  } catch (error: any) {
    // Don't log user rejections as errors
    if (error.code === 4001 || error.message?.includes('User rejected')) {
      console.log('[Phantom] User rejected request')
      return fallback
    }

    // On Android, if we get "Method not found", wait and retry once
    if (isAndroidDevice() && (
      error.message?.includes('Method not found') ||
      error.message?.includes('not a function')
    )) {
      console.log('[Phantom] Method not found on Android, waiting and retrying...')

      // Wait for Phantom to be ready
      const ready = await waitForPhantomReady(3, 1000)

      if (ready) {
        try {
          console.log('[Phantom] Retrying after wait...')
          return await fn()
        } catch (retryError: any) {
          console.warn('[Phantom] Retry failed:', retryError)
        }
      }
    }

    console.warn(errorMessage || '[Phantom] Method call failed:', error)
    return fallback
  }
}

/**
 * Check if Phantom method exists before calling
 */
export const hasPhantomMethod = (phantom: any, methodName: string): boolean => {
  return phantom && typeof phantom[methodName] === 'function'
}
